import React, { useRef } from "react"

type DateInputValue = { dd: string; mm: string; yyyy: string }

type DateInputProps = {
  value?: DateInputValue
  onChange?: (val: DateInputValue) => void
  className?: string
}

const inputCls =
  "w-full border border-slate-200 rounded-md py-1 px-2 text-center focus:outline-none focus:ring-1 focus:ring-blue-400 text-slate-700"

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  className,
}) => {
  const ddRef = useRef<HTMLInputElement>(null)
  const mmRef = useRef<HTMLInputElement>(null)
  const yyyyRef = useRef<HTMLInputElement>(null)

  const handleChange =
    (
      key: "dd" | "mm" | "yyyy",
      nextRef?: React.RefObject<HTMLInputElement>,
      prevRef?: React.RefObject<HTMLInputElement>
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let val = e.target.value.replace(/\D/g, "")
      if (key === "yyyy") val = val.slice(0, 4)
      else val = val.slice(0, 2)

      onChange?.({
        ...(value ?? { dd: "", mm: "", yyyy: "" }),
        [key]: val,
      } as DateInputValue)

      if (
        (key === "yyyy" && val.length === 4 && nextRef?.current) ||
        (key !== "yyyy" && val.length === 2 && nextRef?.current)
      ) {
        nextRef.current.focus()
      } else if (val.length === 0 && prevRef?.current) {
        prevRef.current.focus()
      }
    }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "")
    if (paste.length === 8) {
      onChange?.({
        dd: paste.slice(0, 2),
        mm: paste.slice(2, 4),
        yyyy: paste.slice(4, 8),
      })
    } else if (paste.length === 6) {
      onChange?.({
        dd: paste.slice(0, 2),
        mm: paste.slice(2, 4),
        yyyy: paste.slice(4, 6),
      })
    }
    e.preventDefault()
  }

  return (
    <div
      className={`flex items-center gap-2 text-slate-400 ${className ?? ""}`}
    >
      <input
        ref={ddRef}
        value={value?.dd ?? ""}
        onChange={handleChange("dd", mmRef)}
        onPaste={handlePaste}
        placeholder="DD"
        className={inputCls}
      />
      /
      <input
        ref={mmRef}
        value={value?.mm ?? ""}
        onChange={handleChange("mm", yyyyRef, ddRef)}
        placeholder="MM"
        className={inputCls}
      />
      /
      <input
        ref={yyyyRef}
        value={value?.yyyy ?? ""}
        onChange={handleChange("yyyy", undefined, mmRef)}
        placeholder="YYYY"
        className={inputCls}
      />
    </div>
  )
}

export default DateInput
export type { DateInputValue }
